/**
 * The settings registry. A feature declares its own player setting in a `*.setting.ts` file beside
 * it, with `defineSetting` and a field added to `Settings`:
 *
 *   declare module '../settingsRegistry' {
 *     interface Settings { lighting: boolean }
 *   }
 *   defineSetting({ key: 'lighting', default: true, order: 100, menu: { group: 'visual', label: '…' } });
 *
 * settings.ts loads every `*.setting.ts` before it reads the saved settings, and it and the
 * settings menu (ui/settings.ts) are built from what is declared, in `order`: the defaults, the
 * saved JSON's keys and their order, and the menu's checkboxes.
 */

/**
 * Player preferences (per browser), read by the renderer, effects and audio every frame: one field
 * per declared setting, added by its `*.setting.ts`.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface Settings {}

/** The settings menu's sections: sound (เสียง), visuals (ภาพและเอฟเฟกต์) and data kept in the browser (ข้อมูลในเครื่อง). */
export type SettingGroup = 'sound' | 'visual' | 'data';

export interface SettingDef<K extends keyof Settings = keyof Settings> {
  readonly key: K;
  readonly default: Settings[K];
  /**
   * Where it comes among the settings: in the saved JSON and among its section's rows in the menu
   * (steps of 10 leave room between).
   */
  readonly order: number;
  /** A number held to this range when it is loaded. */
  readonly range?: readonly [number, number];
  /**
   * A row in the settings menu: its section and its Thai label. A checkbox for a true/false setting,
   * or a drop-down for a number setting with `choices`. Without it the menu lays the setting out by
   * hand (the volume sliders), or not at all.
   */
  readonly menu?: SettingMenu<K>;
}

export interface SettingMenu<K extends keyof Settings = keyof Settings> {
  readonly group: SettingGroup;
  readonly label: string;
  /** A drop-down of these (Thai), for a number setting: its value is the index of the one picked. */
  readonly choices?: readonly string[];
  /** The player picked it in the menu: called once it is set, before it is saved (to apply a preset). */
  readonly changed?: (value: Settings[K]) => void;
  /** A short line beside the row (Thai), worked out again after any change in the menu; null for none. */
  readonly note?: () => string | null;
  /** A button at the end of the row (Thai label); the note is worked out again once `run` is done. */
  readonly action?: { readonly label: string; readonly run: () => void | Promise<void> };
}

const defs: SettingDef[] = [];

/** Declares a setting (once, from its feature's `*.setting.ts`). */
export function defineSetting<K extends keyof Settings>(def: SettingDef<K>): SettingDef<K> {
  const all = def as unknown as SettingDef;
  if (defs.some((d) => d.key === all.key)) throw new Error(`Setting "${all.key}" is declared twice`);
  const clash = defs.find((d) => d.order === all.order);
  if (clash) throw new Error(`Settings "${clash.key}" and "${all.key}" share order ${all.order}`);
  if (all.menu && !all.menu.choices && typeof all.default !== 'boolean')
    throw new Error(`Setting "${all.key}" has a checkbox but is not true/false`);
  if (all.menu?.choices && typeof all.default !== 'number')
    throw new Error(`Setting "${all.key}" has choices but is not a number`);
  if (all.range && typeof all.default !== 'number')
    throw new Error(`Setting "${all.key}" has a range but is not a number`);
  defs.push(all);
  defs.sort((a, b) => a.order - b.order);
  return def;
}

/** Every declared setting, in order. */
export const settingDefs = (): readonly SettingDef[] => defs;
